import React, { useState, useEffect } from 'react';
import { Mail, Lock, Info, Eye, EyeOff } from 'lucide-react';
import { Button } from '../ui/Button';
import { FormField } from '../ui/FormField';
import { Alert } from '../ui/Alert';

interface SignInFormProps {
  onSubmit: (email: string, password: string) => Promise<void>;
  loading: boolean;
  error: string;
  success: string;
  onSwitchToSignUp: () => void;
}

export const SignInForm: React.FC<SignInFormProps> = ({
  onSubmit,
  loading,
  error,
  success,
  onSwitchToSignUp,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formTouched, setFormTouched] = useState(false);
  const [forgotPasswordClicked, setForgotPasswordClicked] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [resetError, setResetError] = useState('');

  // Load saved email if remember me was checked
  useEffect(() => {
    const savedEmail = localStorage.getItem('rememberedEmail');
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Save email if remember me is checked
    if (rememberMe) {
      localStorage.setItem('rememberedEmail', email);
    } else {
      localStorage.removeItem('rememberedEmail');
    }
    
    await onSubmit(email, password);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetError('');
    
    if (!resetEmail.trim()) {
      setResetError('Please enter your email address');
      return;
    }
    
    try {
      // In a real app, this would call a password reset API
      console.log('Password reset requested for:', resetEmail);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setResetEmailSent(true);
    } catch (error) {
      setResetError('Failed to send password reset email. Please try again.');
    }
  };

  const fillDemoCredentials = (demoRole: 'admin' | 'client') => {
    if (demoRole === 'admin') {
      setEmail('admin@aicrm.com');
    } else {
      setEmail('client@example.com');
    }
    setPassword('demo123456');
    setFormTouched(true);
  };

  if (forgotPasswordClicked) {
    return (
      <form onSubmit={handleForgotPassword} className="space-y-6 p-6">
        <div className="text-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Reset Password</h3>
          <p className="text-sm text-gray-600">Enter your email to receive a password reset link</p>
        </div>
        
        {resetError && <Alert type="error" message={resetError} />}
        {resetEmailSent && (
          <Alert 
            type="success" 
            message="Password reset instructions have been sent to your email. Please check your inbox."
          />
        )}

        <FormField
          label="Email Address"
          icon={Mail}
          required
        >
          <input
            type="email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
            placeholder="Enter your email"
            required
          />
        </FormField>

        <div className="flex space-x-3">
          <Button
            type="button"
            variant="ghost"
            size="lg"
            className="flex-1"
            onClick={() => setForgotPasswordClicked(false)}
            disabled={loading}
          >
            Back to Login
          </Button>
          <Button
            type="submit"
            size="lg"
            loading={loading}
            className="flex-1 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700"
            disabled={loading || resetEmailSent}
          >
            {resetEmailSent ? 'Email Sent' : 'Send Reset Link'}
          </Button>
        </div>
      </form>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 p-6">
      {error && <Alert type="error" message={error} />}
      {success && <Alert type="success" message={success} />}

      <FormField
        label="Email Address"
        icon={Mail}
        required
      >
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setFormTouched(true);
          }}
          className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
          placeholder="Enter your email"
          required
        />
      </FormField>

      <FormField
        label="Password"
        icon={Lock}
        required
      >
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setFormTouched(true);
            }}
            className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
            placeholder="Enter your password"
            required
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>
      </FormField>

      <div className="flex items-center justify-between">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
          />
          <span className="ml-2 text-sm text-gray-600">Remember me</span>
        </label>
        <button
          type="button"
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          onClick={() => setForgotPasswordClicked(true)}
        >
          Forgot password?
        </button>
      </div>

      <Button
        type="submit"
        size="lg"
        loading={loading}
        className="w-full bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 transform hover:scale-[1.01] transition-all duration-200"
        disabled={loading || (!formTouched && !email && !password)}
      >
        Sign In
      </Button>

      <div className="text-center">
        <button
          type="button"
          onClick={onSwitchToSignUp}
          className="text-primary-600 hover:text-primary-700 text-sm font-medium transition-colors duration-200"
        >
          Don't have an account? Sign up
        </button>
      </div>

      <div className="border-t pt-4">
        <div className="flex space-x-3">
          <Button
            variant="secondary"
            size="sm"
            onClick={() => fillDemoCredentials('admin')}
            className="flex-1"
            type="button"
          >
            Admin Login
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => fillDemoCredentials('client')}
            className="flex-1"
            type="button"
          >
            Client Login
          </Button>
        </div>
        <p className="text-xs text-gray-500 mt-2 text-center">
          Demo password: demo123456
        </p>
      </div>
    </form>
  );
};